import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  flagTrackerOrder,
  getAllTrackerOrders,
  getMergedTrackerOrders,
  getTrackerFlagByKey,
  getTrackerOrderDetails,
  getAllFactoryFlags,
  addFactoryOrderDetails,
  getFactoryOrderDetailsByKey,
  updateFactoryOrderDetails,
  addTrackerOrderEnquiry,
  getTrackerEnquiriesByKey,
  searchTrackerEnquiriesByKey,
  approveIncomingOrder,
  updateIncomingOrder,
  updateChinaOrderPriority,
  addTrackerOrderEnquiryReply,
  updateBulkIncomingOrder,
  approveSingleIncomingOrder,
  getAllChinaTrackerEnquiries,
  getAllChinaOrganizationalUsers,
  updateChinaChecklist,
  searchFactoryEnquiriesByKey,
  fetchRejectedHistoryChinaOrder,
  fetchAllRejectedHistoryChinaOrder,
  getChinaTrackerShipmentDetailsByDetailId,
  createFactoryRunShipment,
  deleteFactoryRunShipment,
  getAllChinaTrackerDetailsWithShipments,
  createDirectFactoryRunShipment,
  updateFactoryRunShipment,
  interruptRunOrderDetail,
  getTrackerInterruptsByKey,
  searchFactoryInterruptsByKey,
  approveCancelledOrder,
  getAllCancelledOrders,
  getChinaShipmentsByContainerNo,
  getChinaShipments,
  generateChinaShipment,
  getChinaIncomingShipments,
  approveIncomingChinaShipment,
  updateGeneratedChinaShipment,
} from "../dispatcher/chinaTrackerDispatcher";
import { getTranslationKeyByValue } from "../../utils/useTranslation";

export const parseDDMMYYYY = (dateStr: any) => {
  if (!dateStr) return null;
  const [day, month, year] = dateStr.split("/").map(Number);
  return new Date(year, month - 1, day);
};

const calculateQtyToShip = (
  totalOrdered: number = 0,
  factoryDetails: any = [],
) => {
  let shipmentQty = 0;
  for (const detail of factoryDetails) {
    if (detail.shipments && detail.shipments.length > 0) {
      for (const shipment of detail.shipments) {
        shipmentQty += shipment.shipmentQty;
      }
    }
  }
  return totalOrdered - shipmentQty;
};

// --- New helper function for order status ---
export const calculateOrderStatus = (
  POStatus: any,
  fulfillmentPercent: any,
  etaStr: any,
  factoryDetails: any[] = [],
  requestDateStr: any,
  QuotationStatus: string | null,
) => {
  if (POStatus === "Closed") return "Closed";
  if (POStatus === "Cancelled") return "Cancelled";

  // If fullfilment is 100, Order is Closed Immediately
  if (fulfillmentPercent === 100) return "Completed";

  const currentDate = new Date();
  const eta = parseDDMMYYYY(etaStr);
  const requestDate = requestDateStr
    ? new Date(requestDateStr.toString().trim())
    : null;

  // If any run orders are interrupted, then return Interrupted status
  for (const detail of factoryDetails) {
    const interrupted: boolean = detail?.interrupted || false;
    if (interrupted) {
      return "Interrupted";
    }
  }

  // If any shipment ETA is ahead of requestDate, mark as Delayed
  if (requestDate) {
    for (const detail of factoryDetails) {
      if (detail.shipments && detail.shipments.length > 0) {
        for (const shipment of detail.shipments) {
          if (shipment.ETA) {
            const shipmentETA = new Date(shipment.ETA);
            if (shipmentETA > requestDate) return "Delayed";
          }
        }
      }
    }
  }

  // If no factory production details, No Data
  if (factoryDetails.length === 0) return "No Data";

  if (factoryDetails.length > 0) {
    for (const factory of factoryDetails) {
      if (!factory.actualStartDay) {
        return "Planned";
      }
    }
  }

  if (eta) {
    const delayedDate = new Date(eta);
    delayedDate.setDate(delayedDate.getDate() + 7);

    const riskDate = new Date(currentDate);
    riskDate.setDate(riskDate.getDate() + 45);

    if (currentDate > delayedDate) return "Delayed";

    // If fulfillment is 0 and riskDate
    if (fulfillmentPercent === 0 && eta <= riskDate) return "At Risk";
  }

  return "On Time";
};

// Generic utility function to filter and optionally sort orders by date
function getOrders(
  orders: any[],
  filterFn: (order: any) => boolean,
  sortByDate = true,
): any[] {
  if (!Array.isArray(orders)) return [];

  const filteredOrders = orders.filter(filterFn);

  if (!sortByDate) return filteredOrders;

  try {
    return filteredOrders.sort((a, b) => {
      const parseDate = (dateStr: string) => {
        const [day, month, year] = dateStr.trim().split("/").map(Number);
        return new Date(year, month - 1, day);
      };

      const dateA = parseDate(a.RequestDate);
      const dateB = parseDate(b.RequestDate);

      return dateA.getTime() - dateB.getTime(); // oldest → newest
    });
  } catch (error) {
    console.error("Failed to sort orders:", error);
    return filteredOrders; // fallback if sort fails
  }
}

// Now your specific functions become simple wrappers:

function getIncomingOrders(orders: any[], sortByDate = true) {
  const non2024Orders = orders.filter((order) => {
    // Prefer OrderYear, fallback to date parsing
    const year = order.OrderYear ?? new Date(order.OrderDate).getFullYear();

    return year !== 2024;
  });

  return getOrders(
    non2024Orders,
    (order) =>
      order.incomingStatus === "INCOMING" &&
      order.OrderStatus !== "Closed" &&
      order.OrderStatus !== "Cancelled",
    sortByDate,
  );
}

function getRejectedOrders(orders: any[], sortByDate = true) {
  return getOrders(
    orders,
    (order) =>
      (order.incomingStatus === "REJECTED" ||
        order.incomingStatus === "REVISED_BY_PURCHASING") &&
      order.OrderStatus !== "Closed" &&
      order.OrderStatus !== "Cancelled",
    sortByDate,
  );
}

function getUnrevisedRejectedOrders(orders: any[], sortByDate = true) {
  return getOrders(
    orders,
    (order) =>
      order.incomingStatus === "REJECTED" &&
      order.OrderStatus !== "Closed" &&
      order.OrderStatus !== "Cancelled",
    sortByDate,
  );
}

function getCancelledOrders(orders: any[], sortByDate = true) {
  return getOrders(
    orders,
    (order) =>
      order.OrderStatus === "Cancelled" || order.OrderStatus === "Closed",
    sortByDate,
  );
}

interface ChinaTrackerState {
  loading: boolean;
  orders: any[];
  orderDetails: any[];
  trackedOrder: any;
  filteredOrders: any[]; // Separate filtered results

  // Flags
  loadingFlag: boolean;
  loadingAddFlag: boolean;
  factoryFlags: any[];

  sortDirection: string;
  loadingEnquiries: boolean;
  loadingAddEnqiry: boolean;
  enquiries: any;
  filteredEnquiries: any;
  filteredFlags: any;
  isChinaTrackerLoaded: boolean;

  incomingOrders: any[];
  approvedOrders: any[];
  rejectedOrders: any[];
  unRevisedRejectedOrders: any[];
  allOrders: any[];
  cancelledOrders: any[];
  cancelledOrdersHistory: any[];

  loadingIncomingAdd: boolean;
  loadingCancel: boolean;

  isLanguageSet: boolean;

  loadingPriority: boolean;

  loadingUsers: boolean;

  // Checklist Specific
  users: any[];
  loadingUpdateChecklist: boolean;

  // reject history
  loadingRejectedHistory: boolean;
  rejectedHistory: any[];

  // Factory Run Orders
  loadingRunOrders: boolean;
  loadingRunOrdersAction: boolean;
  runOrders: any[];

  // Factory Shipment Details
  loadingShipments: boolean;
  loadingShipmentsAction: boolean;
  orderDetailShipments: any[];

  // Run Order Interrupts
  loadingInterrupts: boolean;
  loadingInterruptsAction: boolean;
  interrupts: any[];

  shipments: {
    loading: boolean;
    shipments: any[];
    shipment: any;
    shipmentsCount: number;
    loadingAction: boolean;

    chinaShipments: any[];
    loadingChinaShipments: boolean;
    enrichedShipments: any[];

    totalCount: number | null;
    totalPages: number | null;
    pageSize: number | null;
    page: number | null;
    sort: any;
    status: string;
    search: string;
    dateFrom: string | null;
    dateTo: string | null;

    incoming: {
      loading: boolean;
      shipments: any[];
      shipment: any;
      shipmentsCount: number;
      loadingAction: boolean;

      chinaShipments: any[];
      loadingChinaShipments: boolean;
      enrichedShipments: any[];

      totalCount: number | null;
      totalPages: number | null;
      pageSize: number | null;
      page: number | null;
      sort: any;
      status: string;
      search: string;
    };
  };
}

const initialState: ChinaTrackerState = {
  loading: false,
  orders: [],
  trackedOrder: null,
  orderDetails: [],
  filteredOrders: [], // Maintain filtered orders separately
  loadingAddFlag: false,
  loadingFlag: false,
  sortDirection: "desc",
  factoryFlags: [],

  loadingAddEnqiry: false,
  loadingEnquiries: false,
  enquiries: [],
  filteredEnquiries: [],
  filteredFlags: [],
  isChinaTrackerLoaded: false,

  allOrders: [],
  cancelledOrders: [],
  cancelledOrdersHistory: [],
  incomingOrders: [],
  approvedOrders: [],
  rejectedOrders: [],
  unRevisedRejectedOrders: [],

  loadingIncomingAdd: false,
  loadingCancel: false,
  isLanguageSet: false,

  loadingPriority: false,
  loadingUsers: false,

  // Checklist Specific
  users: [],
  loadingUpdateChecklist: false,

  // reject history
  loadingRejectedHistory: false,
  rejectedHistory: [],

  // Factory Run Orders
  loadingRunOrders: false,
  loadingRunOrdersAction: false,
  runOrders: [],

  // Factory Shipment Details
  loadingShipments: false,
  loadingShipmentsAction: false,
  orderDetailShipments: [],

  // Run Order Interrupts
  loadingInterrupts: false,
  loadingInterruptsAction: false,
  interrupts: [],

  // China Shipment
  shipments: {
    loading: false,
    shipments: [],
    shipment: null,
    shipmentsCount: 0,
    loadingAction: false,

    chinaShipments: [],
    loadingChinaShipments: false,
    enrichedShipments: [],

    totalCount: null,
    totalPages: null,
    pageSize: 10,
    page: 1,
    sort: {
      key: "createdAt",
      direction: "desc",
    },
    status: "all",
    search: "",
    dateFrom: null,
    dateTo: null,

    incoming: {
      loading: false,
      shipments: [],
      shipment: null,
      shipmentsCount: 0,
      loadingAction: false,

      chinaShipments: [],
      loadingChinaShipments: false,
      enrichedShipments: [],

      totalCount: null,
      totalPages: null,
      pageSize: 10,
      page: 1,
      sort: {
        key: "createdAt",
        direction: "desc",
      },
      status: "all",
      search: "",
    },
  },
};

// Create a slice for actions and reducers
export const chinaTrackerSlice = createSlice({
  name: "chinaTracker",
  initialState,
  reducers: {
    updateSort: (state, action: PayloadAction<any>) => {
      state.shipments.sort = action.payload;
    },
    updateStatus: (state, action: PayloadAction<string>) => {
      state.shipments.status = action.payload;
    },
    updatePage: (state, action: PayloadAction<number>) => {
      state.shipments.page = action.payload;
    },
    updatePageSize: (state, action: PayloadAction<number>) => {
      state.shipments.pageSize = action.payload;
    },
    searchShipment: (state, action: PayloadAction<string>) => {
      state.shipments.search = action.payload;
    },

    // Incoming China Shipments
    updateIncomingSort: (state, action: PayloadAction<any>) => {
      state.shipments.incoming.sort = action.payload;
    },
    updateIncomingStatus: (state, action: PayloadAction<string>) => {
      state.shipments.incoming.status = action.payload;
    },
    updateIncomingPage: (state, action: PayloadAction<number>) => {
      state.shipments.incoming.page = action.payload;
    },
    updateIncomingPageSize: (state, action: PayloadAction<number>) => {
      state.shipments.incoming.pageSize = action.payload;
    },
    searchIncomingShipment: (state, action: PayloadAction<string>) => {
      state.shipments.incoming.search = action.payload;
    },

    searchTrackerOrders: (state, action: PayloadAction<string>) => {
      const searchTerm = action.payload.toLowerCase();

      state.filteredOrders = state.approvedOrders.filter((order: any) => {
        // Convert null or undefined OrderNumber1 to an empty string before applying search
        const orderNumber = order?.OrderNumber1
          ? String(order.OrderNumber1).toLowerCase()
          : "";

        const itemName = order?.ItemName?.toString()?.toLowerCase()
          ? String(order.ItemName)?.toLowerCase()
          : "";

        const itemCode = order?.ItemCode?.toString()?.toLowerCase()
          ? String(order.ItemCode)?.toLowerCase()
          : "";

        const msRef = order?.MSRef?.toString()?.toLowerCase()
          ? String(order.MSRef)?.toLowerCase()
          : "";

        return (
          orderNumber.includes(searchTerm) ||
          msRef.includes(searchTerm) ||
          itemName.includes(searchTerm) ||
          itemCode.includes(searchTerm)
        );
      });
    },
    searchIncomingTrackerOrders: (state, action: PayloadAction<string>) => {
      const searchTerm = action.payload.toLowerCase();
      const baseOrders = getIncomingOrders(state.orders);

      state.incomingOrders = baseOrders.filter((order: any) => {
        // Convert null or undefined OrderNumber1 to an empty string before applying search
        const orderNumber = order?.OrderNumber1
          ? String(order.OrderNumber1).toLowerCase()
          : "";
        const msRef = order?.MSRef?.toString()?.toLowerCase()
          ? String(order.MSRef)?.toLowerCase()
          : "";
        return orderNumber.includes(searchTerm) || msRef.includes(searchTerm);
      });
    },
    resetIncomingSearch: (state) => {
      state.incomingOrders = getIncomingOrders(state.orders);
    },

    searchRejectedTrackerOrders: (state, action: PayloadAction<string>) => {
      const searchTerm = action.payload.toLowerCase();
      const baseOrders = getRejectedOrders(state.orders);

      state.rejectedOrders = baseOrders.filter((order: any) => {
        // Convert null or undefined OrderNumber1 to an empty string before applying search
        const orderNumber = order?.OrderNumber1
          ? String(order.OrderNumber1).toLowerCase()
          : "";
        const msRef = order?.MSRef?.toString()?.toLowerCase()
          ? String(order.MSRef)?.toLowerCase()
          : "";
        return orderNumber.includes(searchTerm) || msRef.includes(searchTerm);
      });
    },
    resetRejectedSearch: (state) => {
      state.rejectedOrders = getRejectedOrders(state.orders);
    },

    searchUnRevisedRejectedTrackerOrders: (
      state,
      action: PayloadAction<string>,
    ) => {
      const searchTerm = action.payload.toLowerCase();
      const baseOrders = getUnrevisedRejectedOrders(state.orders);

      state.unRevisedRejectedOrders = baseOrders.filter((order: any) => {
        // Convert null or undefined OrderNumber1 to an empty string before applying search
        const orderNumber = order?.OrderNumber1
          ? String(order.OrderNumber1).toLowerCase()
          : "";
        const msRef = order?.MSRef?.toString()?.toLowerCase()
          ? String(order.MSRef)?.toLowerCase()
          : "";
        return orderNumber.includes(searchTerm) || msRef.includes(searchTerm);
      });
    },
    resetUnrevisedRejectedSearch: (state) => {
      state.unRevisedRejectedOrders = getUnrevisedRejectedOrders(state.orders);
    },

    // Search Cancelled Orders
    searchCancelledTrackerOrders: (state, action: PayloadAction<string>) => {
      const searchTerm = action.payload.toLowerCase();
      const baseOrders = getCancelledOrders(state.orders);

      state.cancelledOrders = baseOrders.filter((order: any) => {
        // Convert null or undefined OrderNumber1 to an empty string before applying search
        const orderNumber = order?.OrderNumber1
          ? String(order.OrderNumber1).toLowerCase()
          : "";
        const msRef = order?.MSRef?.toString()?.toLowerCase()
          ? String(order.MSRef)?.toLowerCase()
          : "";
        return orderNumber.includes(searchTerm) || msRef.includes(searchTerm);
      });
    },
    resetCancelledSearch: (state) => {
      state.cancelledOrders = getCancelledOrders(state.orders);
    },

    // Search Cancelled Orders Audit
    searchCancelledTrackerAuditOrders: (
      state,
      action: PayloadAction<string>,
    ) => {
      const searchTerm = action.payload.toLowerCase();

      state.cancelledOrdersHistory = state.cancelledOrdersHistory.filter(
        (order: any) => {
          // Convert null or undefined OrderNumber1 to an empty string before applying search
          const orderNumber = order?.orderNumber
            ? String(order.orderNumber).toLowerCase()
            : "";
          const msRef = order?.MSRef?.toString()?.toLowerCase()
            ? String(order.MSRef)?.toLowerCase()
            : "";
          return orderNumber.includes(searchTerm) || msRef.includes(searchTerm);
        },
      );
    },

    searchAllTrackerOrders: (state, action: PayloadAction<string>) => {
      const searchTerm = action.payload.toLowerCase();

      state.allOrders = state.orders.filter((order: any) => {
        const status = order?.OrderStatus?.toLowerCase();
        if (status === "cancelled" || status === "closed") {
          return false;
        }

        // Convert null or undefined OrderNumber1 to an empty string before applying search
        const orderNumber = order?.OrderNumber1
          ? String(order.OrderNumber1).toLowerCase()
          : "";
        const msRef = order?.MSRef?.toString()?.toLowerCase()
          ? String(order.MSRef)?.toLowerCase()
          : "";
        return orderNumber.includes(searchTerm) || msRef.includes(searchTerm);
      });
    },
    resetAllSearch: (state) => {
      state.allOrders = state.orders;
    },

    filterAllSearch: (state, action) => {
      const filterStatus = action.payload.toLowerCase();
      if (filterStatus === "all") {
        state.allOrders = state.orders;
        return;
      }

      state.allOrders = state.orders.filter((order: any) => {
        const orderStatus = order?.OrderStatus?.toLowerCase?.() || "";
        const incomingStatus = order?.incomingStatus?.toLowerCase?.() || "";

        if (["incoming", "approved"].includes(filterStatus)) {
          return incomingStatus === filterStatus;
        }

        return orderStatus === filterStatus;
      });
    },
    resetSearch: (state, action: PayloadAction<any>) => {
      const {
        item: filterStatusTranslated,
        productionLine: filterProductionLine,
        language,
      } = action.payload;

      const filterStatusKey = getTranslationKeyByValue(
        filterStatusTranslated,
        language,
      );

      state.filteredOrders = state.approvedOrders.filter((order: any) => {
        const orderStatus = String(order?.OrderStatus || "");
        const productionLine = order?.allocateProductionLine;

        const matchesStatus =
          !filterStatusKey ||
          filterStatusKey === "All" ||
          orderStatus === filterStatusKey;

        const matchesProductionLine =
          filterProductionLine === "All" ||
          (Array.isArray(productionLine) &&
            productionLine.includes(filterProductionLine));

        return matchesStatus && matchesProductionLine;
      });
    },
    filterTrackerOrders: (state, action: PayloadAction<any>) => {
      const {
        item: filterStatusTranslated,
        items: filterStatusArray,
        productionLine: filterProductionLine,
        productionLines: filterProductionLineArray,
        language,
      } = action.payload;

      // Resolve status filter(s)
      let matchesStatusFn: (orderStatus: string) => boolean;

      if (
        filterStatusArray &&
        Array.isArray(filterStatusArray) &&
        filterStatusArray.length > 0
      ) {
        const filterStatusKeys = filterStatusArray.map(
          (s: string) => getTranslationKeyByValue(s, language) || s,
        );
        matchesStatusFn = (orderStatus: string) =>
          filterStatusKeys.includes(orderStatus);
      } else {
        const filterStatusKey = getTranslationKeyByValue(
          filterStatusTranslated,
          language,
        );
        const isAllStatus =
          !filterStatusKey || ["All", "all", "全部"].includes(filterStatusKey);
        matchesStatusFn = (orderStatus: string) =>
          isAllStatus || orderStatus === filterStatusKey;
      }

      // Resolve production line filter(s)
      let matchesProductionLineFn: (productionLine: any) => boolean;

      if (
        filterProductionLineArray &&
        Array.isArray(filterProductionLineArray) &&
        filterProductionLineArray.length > 0
      ) {
        matchesProductionLineFn = (productionLine: any) =>
          Array.isArray(productionLine) &&
          productionLine.some((pl: any) =>
            filterProductionLineArray.includes(pl),
          );
      } else {
        const isAllProductionLine =
          !filterProductionLine ||
          ["All", "全部"].includes(String(filterProductionLine));
        matchesProductionLineFn = (productionLine: any) =>
          isAllProductionLine ||
          (Array.isArray(productionLine) &&
            productionLine.includes(filterProductionLine));
      }

      state.filteredOrders = state.approvedOrders.filter((order: any) => {
        const orderStatus = String(order?.OrderStatus || "");
        const productionLine = order?.allocateProductionLine;
        return (
          matchesStatusFn(orderStatus) &&
          matchesProductionLineFn(productionLine)
        );
      });
    },
    sortTrackerOrdersByOrderNumber: (
      state,
      action: PayloadAction<"asc" | "desc">,
    ) => {
      const direction = action.payload;

      state.filteredOrders = [...state.filteredOrders].sort(
        (a: any, b: any) => {
          const orderA = a?.OrderNumber1 ?? "";
          const orderB = b?.OrderNumber1 ?? "";

          if (orderA < orderB) return direction === "asc" ? -1 : 1;
          if (orderA > orderB) return direction === "asc" ? 1 : -1;
          return 0;
        },
      );
      state.sortDirection = action.payload;
    },
    resetEnquiries: (state) => {
      state.filteredEnquiries = state.enquiries; // Reset to full orders list
      state.factoryFlags = state.factoryFlags;
    },
    enrichChinaShipments: (state) => {
      const rawShipments = state.shipments.chinaShipments;
      const enrichedShipments = rawShipments.map((shipment: any) => {
        const { key, orderNumber } = shipment.factoryOrderDetail || {};

        const targetOrder = state.orders.find(
          (order: any) =>
            order.Key === key && order.OrderNumber1 === orderNumber,
        );

        return {
          ...shipment,
          factoryOrderDetail: {
            ...shipment.factoryOrderDetail,
            trackerOrder: targetOrder || null, // attach matched order (or null if not found)
          },
        };
      });
      state.shipments.enrichedShipments = enrichedShipments;
    },
    enrichChinaIncomingShipments: (state) => {
      const rawShipments = state.shipments.incoming.chinaShipments;
      const enrichedShipments = rawShipments.map((shipment: any) => {
        const { key, orderNumber } = shipment.factoryOrderDetail || {};

        const targetOrder = state.orders.find(
          (order: any) =>
            order.Key === key && order.OrderNumber1 === orderNumber,
        );

        return {
          ...shipment,
          factoryOrderDetail: {
            ...shipment.factoryOrderDetail,
            trackerOrder: targetOrder || null, // attach matched order (or null if not found)
          },
        };
      });
      state.shipments.incoming.enrichedShipments = enrichedShipments;
    },
    filterSpecificField: (
      state,
      action: PayloadAction<{ filterField: string; filterValue: any }>,
    ) => {
      const { filterField, filterValue } = action.payload;
      state.filteredOrders = state.filteredOrders.filter((order: any) => {
        return order?.[filterField] === filterValue;
      });
    },
    setChinaLanguage: (state) => {
      state.isLanguageSet = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllTrackerOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllTrackerOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.data?.recordsets || [];
        state.filteredOrders = state.orders; // Initialize filtered orders
      })
      .addCase(getAllTrackerOrders.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getTrackerOrderDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTrackerOrderDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.orderDetails = action.payload.data?.recordsets || [];
      })
      .addCase(getTrackerOrderDetails.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getMergedTrackerOrders.pending, (state, action) => {
        if (action.meta.arg.showLoading !== false) {
          state.loading = true;
        } else {
          state.loadingIncomingAdd = true;
        }
      })
      .addCase(getMergedTrackerOrders.fulfilled, (state, action) => {
        if (action.payload.showLoading !== false) {
          state.loading = false;
        } else {
          state.loadingIncomingAdd = false;
        }
        state.orders = action.payload.data || [];

        // Filter orders by incomingStatus
        state.incomingOrders = getIncomingOrders(state.orders);

        state.approvedOrders = state.orders.filter(
          (order) =>
            order.incomingStatus === "APPROVED" &&
            order.OrderStatus != "Cancelled" &&
            order.OrderStatus != "Closed",
        );

        state.rejectedOrders = getRejectedOrders(state.orders);

        state.unRevisedRejectedOrders = getUnrevisedRejectedOrders(
          state.orders,
        );

        state.cancelledOrders = getCancelledOrders(state.orders);

        // If you want filteredOrders to default to approvedOrders (for main dashboard)
        state.filteredOrders = state.approvedOrders;

        state.allOrders = state.orders;

        state.isChinaTrackerLoaded = true;
      })
      .addCase(getMergedTrackerOrders.rejected, (state, action) => {
        if (action.meta.arg.showLoading !== false) {
          state.loading = false;
        } else {
          state.loadingIncomingAdd = false;
        }
      })
      .addCase(flagTrackerOrder.pending, (state) => {
        state.loadingAddFlag = true;
      })
      .addCase(flagTrackerOrder.fulfilled, (state, action) => {
        state.loadingAddFlag = false;
      })
      .addCase(flagTrackerOrder.rejected, (state) => {
        state.loadingAddFlag = false;
      })

      .addCase(getTrackerFlagByKey.pending, (state) => {
        state.loadingFlag = true;
      })
      .addCase(getTrackerFlagByKey.fulfilled, (state, action) => {
        state.loadingFlag = false;
        state.factoryFlags = action.payload;
      })
      .addCase(getTrackerFlagByKey.rejected, (state) => {
        state.loadingFlag = false;
      })

      .addCase(getAllFactoryFlags.pending, (state) => {
        state.loadingFlag = true;
      })
      .addCase(getAllFactoryFlags.fulfilled, (state, action) => {
        state.loadingFlag = false;
        state.factoryFlags = action.payload;
      })
      .addCase(getAllFactoryFlags.rejected, (state) => {
        state.loadingFlag = false;
      })

      .addCase(addFactoryOrderDetails.pending, (state) => {
        state.loadingRunOrdersAction = true;
      })
      .addCase(addFactoryOrderDetails.fulfilled, (state, action) => {
        state.loadingRunOrdersAction = false;
        const { key, orderNumber } = action.payload.data;
        // Find the order in state.orders
        const targetOrder = state.approvedOrders.find(
          (order: any) =>
            order.Key === key && order.OrderNumber1 === orderNumber,
        );
        if (targetOrder) {
          // Initialize details array if it doesn't exist
          if (!Array.isArray(targetOrder.details)) {
            targetOrder.details = [];
          }
          // Push the new detail to the details array
          targetOrder.details.push(action.payload.data);

          //  Use helper function here
          targetOrder.OrderStatus = calculateOrderStatus(
            targetOrder.POStatus,
            targetOrder.fulfillmentPercent || 0,
            targetOrder.ETA,
            targetOrder.details,
            targetOrder.RequestDate,
            targetOrder.QuotationStatus || null,
          );

          const allocateProductionLines = [
            ...new Set(
              targetOrder.details
                .map((item: any) => item.allocateProductionLine)
                .filter(
                  (v: any) =>
                    v !== null && v !== undefined && v !== "" && v !== 99,
                ), // only filter undefined,empty or null values
            ),
          ];

          if (allocateProductionLines.length > 0) {
            targetOrder.allocateProductionLine = allocateProductionLines;
          }
        }

        // Find and update the order in filteredOrders if it exists there too
        const targetFilteredOrder = state.filteredOrders.find(
          (order: any) =>
            order.Key === key && order.OrderNumber1 === orderNumber,
        );
        if (targetFilteredOrder && targetOrder) {
          targetFilteredOrder.details = [...targetOrder.details];
          targetFilteredOrder.OrderStatus = targetOrder.OrderStatus;
          targetFilteredOrder.allocateProductionLine =
            targetOrder.allocateProductionLine;
        }
      })
      .addCase(addFactoryOrderDetails.rejected, (state) => {
        state.loadingRunOrdersAction = false;
      })

      // Get Run Orders By Key
      .addCase(getFactoryOrderDetailsByKey.pending, (state) => {
        state.loadingRunOrders = true;
      })
      .addCase(getFactoryOrderDetailsByKey.fulfilled, (state, action) => {
        state.loadingRunOrders = false;
        state.runOrders = action.payload;
      })
      .addCase(getFactoryOrderDetailsByKey.rejected, (state) => {
        state.loadingRunOrders = false;
      })

      .addCase(updateFactoryOrderDetails.pending, (state) => {
        state.loadingRunOrdersAction = true;
      })
      .addCase(updateFactoryOrderDetails.fulfilled, (state, action) => {
        state.loadingRunOrdersAction = false;

        const updatedDetail = action.payload.data;
        const { key, orderNumber } = updatedDetail;

        const targetOrder = state.approvedOrders.find(
          (order: any) =>
            order.Key === key && order.OrderNumber1 === orderNumber,
        );

        if (targetOrder) {
          if (!Array.isArray(targetOrder.details)) {
            targetOrder.details = [];
          }

          const detailIndex = targetOrder.details.findIndex(
            (detail: any) => detail._id === updatedDetail._id,
          );

          if (detailIndex !== -1) {
            targetOrder.details[detailIndex] = updatedDetail;
          } else {
            targetOrder.details.push(updatedDetail);
          }

          targetOrder.details.sort(
            (a: any, b: any) =>
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
          );

          //  Use helper function here
          targetOrder.OrderStatus = calculateOrderStatus(
            targetOrder.POStatus,
            targetOrder.fulfillmentPercent || 0,
            targetOrder.ETA,
            targetOrder.details,
            targetOrder.RequestDate,
            targetOrder.QuotationStatus || null,
          );

          // Recalculate allocateProductionLine array
          const allocateProductionLines = [
            ...new Set(
              targetOrder.details
                .map((item: any) => item.allocateProductionLine)
                .filter(
                  (v: any) =>
                    v !== null && v !== undefined && v !== "" && v !== 99,
                ), // only filter undefined,empty or null values
            ),
          ];

          targetOrder.allocateProductionLine =
            allocateProductionLines.length > 0 ? allocateProductionLines : [];
        }

        // Reflect updates in filteredOrders without overwriting the entire array
        const targetFilteredOrder = state.filteredOrders.find(
          (order: any) =>
            order.Key === key && order.OrderNumber1 === orderNumber,
        );
        if (targetFilteredOrder && targetOrder) {
          targetFilteredOrder.details = [...targetOrder.details];
          targetFilteredOrder.OrderStatus = targetOrder.OrderStatus;
          targetFilteredOrder.allocateProductionLine =
            targetOrder.allocateProductionLine;
        }
      })
      .addCase(updateFactoryOrderDetails.rejected, (state) => {
        state.loadingRunOrdersAction = false;
      })
      .addCase(addTrackerOrderEnquiry.pending, (state) => {
        state.loadingAddEnqiry = true;
      })
      .addCase(addTrackerOrderEnquiry.fulfilled, (state, action) => {
        state.loadingAddEnqiry = false;
        state.enquiries = [...state.enquiries, action.payload.data];
      })
      .addCase(addTrackerOrderEnquiry.rejected, (state) => {
        state.loadingAddEnqiry = false;
      })

      .addCase(addTrackerOrderEnquiryReply.pending, (state) => {
        state.loadingAddEnqiry = true;
      })
      .addCase(addTrackerOrderEnquiryReply.fulfilled, (state, action) => {
        state.loadingAddEnqiry = false;
        const { enquiryRef } = action.payload.data;

        const targetEnquiry = state.enquiries.find(
          (enquiry: any) =>
            enquiry._id === enquiryRef && enquiry.enquiryRef == null,
        );

        if (targetEnquiry) {
          targetEnquiry.childEnquiries = [
            ...targetEnquiry.childEnquiries,
            action.payload.data,
          ];
        }
      })
      .addCase(addTrackerOrderEnquiryReply.rejected, (state) => {
        state.loadingAddEnqiry = false;
      })

      // Get Tracker Enquiries by Key
      .addCase(getTrackerEnquiriesByKey.pending, (state) => {
        state.loadingEnquiries = true;
      })
      .addCase(getTrackerEnquiriesByKey.fulfilled, (state, action) => {
        state.loadingEnquiries = false;
        state.enquiries = action.payload;
        state.filteredEnquiries = state.enquiries;
      })
      .addCase(getTrackerEnquiriesByKey.rejected, (state) => {
        state.loadingEnquiries = false;
      })

      //  Search Enquiries
      .addCase(searchTrackerEnquiriesByKey.pending, (state) => {
        state.loadingEnquiries = false;
      })
      .addCase(searchTrackerEnquiriesByKey.fulfilled, (state, action) => {
        state.loadingEnquiries = false;
        state.enquiries = action.payload;
        state.filteredEnquiries = state.enquiries;
      })
      .addCase(searchTrackerEnquiriesByKey.rejected, (state) => {
        state.loadingEnquiries = false;
      })

      // Search Flags
      .addCase(searchFactoryEnquiriesByKey.pending, (state) => {
        state.loadingFlag = false;
      })
      .addCase(searchFactoryEnquiriesByKey.fulfilled, (state, action) => {
        state.loadingFlag = false;
        state.factoryFlags = action.payload;
        state.filteredFlags = state.factoryFlags;
      })
      .addCase(searchFactoryEnquiriesByKey.rejected, (state) => {
        state.loadingFlag = false;
      })

      .addCase(approveIncomingOrder.pending, (state) => {
        state.loadingIncomingAdd = true;
      })
      .addCase(approveIncomingOrder.fulfilled, (state, action) => {
        state.loadingIncomingAdd = false;

        const addedOrders = action.payload.data;

        if (!Array.isArray(addedOrders)) return;

        // Update incomingStatus for each added order
        addedOrders.forEach((addedOrder: any) => {
          const { key, status } = addedOrder;

          const orderIndex = state.orders.findIndex(
            (order: any) => order.Key === key,
          );

          if (orderIndex !== -1) {
            state.orders[orderIndex].incomingStatus = status;
          }
        });

        // Recompute derived lists based on updated statuses
        state.incomingOrders = getIncomingOrders(state.orders);

        state.approvedOrders = state.orders.filter(
          (order) =>
            order.incomingStatus === "APPROVED" &&
            order.OrderStatus != "Cancelled" &&
            order.OrderStatus != "Closed",
        );

        state.rejectedOrders = getRejectedOrders(state.orders);

        state.unRevisedRejectedOrders = getUnrevisedRejectedOrders(
          state.orders,
        );

        state.cancelledOrders = getCancelledOrders(state.orders);

        state.allOrders = state.orders;

        // Default filtered view (you can change based on tab or current UI context)
        state.filteredOrders = state.approvedOrders;
      })
      .addCase(approveIncomingOrder.rejected, (state) => {
        state.loadingIncomingAdd = false;
      })

      // apporove single incoming order

      .addCase(approveSingleIncomingOrder.pending, (state) => {
        state.loadingIncomingAdd = true;
      })
      .addCase(approveSingleIncomingOrder.fulfilled, (state, action) => {
        state.loadingIncomingAdd = false;

        const addedOrder = action.payload.data;
        const { key, status } = addedOrder;

        // Determine target list (approved or rejected)
        const orderIndex = state.orders.findIndex(
          (order: any) => order.Key === key,
        );

        if (orderIndex !== -1) {
          state.orders[orderIndex].incomingStatus = status;

          state.incomingOrders = getIncomingOrders(state.orders);

          state.approvedOrders = state.orders.filter(
            (order) =>
              order.incomingStatus === "APPROVED" &&
              order.OrderStatus != "Cancelled" &&
              order.OrderStatus != "Closed",
          );

          state.rejectedOrders = getRejectedOrders(state.orders);

          state.unRevisedRejectedOrders = getUnrevisedRejectedOrders(
            state.orders,
          );
          state.cancelledOrders = getCancelledOrders(state.orders);
          state.allOrders = state.orders;

          state.filteredOrders = state.approvedOrders;
        }
      })
      .addCase(approveSingleIncomingOrder.rejected, (state) => {
        state.loadingIncomingAdd = false;
      })

      .addCase(updateIncomingOrder.pending, (state) => {
        state.loadingIncomingAdd = true;
      })
      .addCase(updateIncomingOrder.fulfilled, (state, action) => {
        state.loadingIncomingAdd = false;

        const addedOrder = action.payload.data;
        const { key, status } = addedOrder;

        // Determine target list (approved or rejected)
        const orderIndex = state.orders.findIndex(
          (order: any) => order.Key === key,
        );

        if (orderIndex !== -1) {
          state.orders[orderIndex].incomingStatus = status;

          state.incomingOrders = getIncomingOrders(state.orders);

          state.approvedOrders = state.orders.filter(
            (order) =>
              order.incomingStatus === "APPROVED" &&
              order.OrderStatus != "Cancelled" &&
              order.OrderStatus != "Closed",
          );

          state.rejectedOrders = getRejectedOrders(state.orders);

          state.unRevisedRejectedOrders = getUnrevisedRejectedOrders(
            state.orders,
          );

          state.cancelledOrders = getCancelledOrders(state.orders);

          state.allOrders = state.orders;

          state.filteredOrders = state.approvedOrders;
        }
      })
      .addCase(updateIncomingOrder.rejected, (state) => {
        state.loadingIncomingAdd = false;
      })

      .addCase(updateBulkIncomingOrder.pending, (state) => {
        state.loadingIncomingAdd = true;
      })
      .addCase(updateBulkIncomingOrder.fulfilled, (state, action) => {
        state.loadingIncomingAdd = false;

        const addedOrders = action.payload.data;

        if (!Array.isArray(addedOrders)) return;

        // Update incomingStatus for each added order
        addedOrders.forEach((addedOrder: any) => {
          const { key, status } = addedOrder;

          const orderIndex = state.orders.findIndex(
            (order: any) => order.Key === key,
          );

          if (orderIndex !== -1) {
            state.orders[orderIndex].incomingStatus = status;
          }
        });

        // Recompute derived lists based on updated statuses
        state.incomingOrders = getIncomingOrders(state.orders);

        state.approvedOrders = state.orders.filter(
          (order) =>
            order.incomingStatus === "APPROVED" &&
            order.OrderStatus != "Cancelled" &&
            order.OrderStatus != "Closed",
        );

        state.rejectedOrders = getRejectedOrders(state.orders);

        state.unRevisedRejectedOrders = getUnrevisedRejectedOrders(
          state.orders,
        );

        state.cancelledOrders = getCancelledOrders(state.orders);
        state.allOrders = state.orders;

        // Default filtered view (you can change based on tab or current UI context)
        state.filteredOrders = state.approvedOrders;
      })
      .addCase(updateBulkIncomingOrder.rejected, (state) => {
        state.loadingIncomingAdd = false;
      })

      .addCase(updateChinaOrderPriority.pending, (state) => {
        state.loadingPriority = false;
      })
      .addCase(updateChinaOrderPriority.fulfilled, (state, action) => {
        state.loadingPriority = false;
        const { key, orderNumber, priority } = action.payload;
        // Find the order in state.orders
        const targetOrder = state.approvedOrders.find(
          (order: any) =>
            order.Key === key && order.OrderNumber1 === orderNumber,
        );
        if (targetOrder) {
          targetOrder.PriorityOrder = priority;
        }

        const targetFilteredOrder = state.filteredOrders.find(
          (order: any) =>
            order.Key === key && order.OrderNumber1 === orderNumber,
        );
        if (targetFilteredOrder && targetOrder) {
          targetFilteredOrder.PriorityOrder = targetOrder.PriorityOrder;
        }
      })
      .addCase(updateChinaOrderPriority.rejected, (state) => {
        state.loadingPriority = false;
      })

      // Checklist
      // Get All Tracker Enquiries
      .addCase(getAllChinaTrackerEnquiries.pending, (state) => {
        state.loadingEnquiries = true;
      })
      .addCase(getAllChinaTrackerEnquiries.fulfilled, (state, action) => {
        state.loadingEnquiries = false;
        state.enquiries = action.payload.data;
        state.filteredEnquiries = state.enquiries;
      })
      .addCase(getAllChinaTrackerEnquiries.rejected, (state) => {
        state.loadingEnquiries = false;
      })
      // Organization Users Dispatchers
      .addCase(getAllChinaOrganizationalUsers.pending, (state) => {
        state.loadingUsers = true;
      })
      .addCase(getAllChinaOrganizationalUsers.fulfilled, (state, action) => {
        state.loadingUsers = false;
        state.users = action.payload.data;
      })
      .addCase(getAllChinaOrganizationalUsers.rejected, (state) => {
        state.loadingUsers = false;
      })

      // Update Checklist
      .addCase(updateChinaChecklist.pending, (state) => {
        state.loadingUpdateChecklist = true;
      })
      .addCase(updateChinaChecklist.fulfilled, (state, action) => {
        state.loadingUpdateChecklist = false;

        const { data, section } = action.payload;

        if (!data || !data._id) return;

        if (section === "enquiries") {
          const index = state.enquiries.findIndex(
            (enquiry: any) => enquiry._id === data._id,
          );

          if (index !== -1) {
            state.enquiries[index] = data;
          }
        } else if (section === "flagged") {
          const index = state.factoryFlags.findIndex(
            (flag: any) => flag._id === data._id,
          );

          if (index !== -1) {
            state.factoryFlags[index] = data;
          }
        } else if (section === "rejected") {
          const index = state.rejectedHistory.findIndex(
            (flag: any) => flag._id === data._id,
          );

          if (index !== -1) {
            state.rejectedHistory[index] = data;
          }
        }
      })
      .addCase(updateChinaChecklist.rejected, (state) => {
        state.loadingUpdateChecklist = false;
      })

      // Rejected History
      .addCase(fetchRejectedHistoryChinaOrder.pending, (state) => {
        state.loadingRejectedHistory = true;
      })
      .addCase(fetchRejectedHistoryChinaOrder.fulfilled, (state, action) => {
        state.loadingRejectedHistory = false;
        state.rejectedHistory = action.payload;
      })
      .addCase(fetchRejectedHistoryChinaOrder.rejected, (state) => {
        state.loadingRejectedHistory = false;
      })

      .addCase(fetchAllRejectedHistoryChinaOrder.pending, (state) => {
        state.loadingRejectedHistory = true;
      })
      .addCase(fetchAllRejectedHistoryChinaOrder.fulfilled, (state, action) => {
        state.loadingRejectedHistory = false;
        state.rejectedHistory = action.payload;
      })
      .addCase(fetchAllRejectedHistoryChinaOrder.rejected, (state) => {
        state.loadingRejectedHistory = false;
      })

      // Factory Shipment Details
      .addCase(getChinaTrackerShipmentDetailsByDetailId.pending, (state) => {
        state.loadingShipments = true;
      })
      .addCase(
        getChinaTrackerShipmentDetailsByDetailId.fulfilled,
        (state, action) => {
          state.loadingShipments = false;
          state.orderDetailShipments = action.payload;
        },
      )
      .addCase(getChinaTrackerShipmentDetailsByDetailId.rejected, (state) => {
        state.loadingShipments = false;
      })
      .addCase(createFactoryRunShipment.pending, (state) => {
        state.loadingShipmentsAction = true;
      })
      .addCase(createFactoryRunShipment.fulfilled, (state, action) => {
        state.loadingShipmentsAction = false;

        const { key, orderNumber, factoryOrderData } = action.payload.data;

        // Find the order in state.orders
        const targetOrder = state.approvedOrders.find(
          (order: any) =>
            order.Key === key && order.OrderNumber1 === orderNumber,
        );
        if (targetOrder) {
          // Initialize details array if it doesn't exist
          if (!Array.isArray(targetOrder.details)) {
            targetOrder.details = [];
          }
          // Push the new detail to the details array
          targetOrder.details = factoryOrderData;

          //  Use helper function here
          targetOrder.OrderStatus = calculateOrderStatus(
            targetOrder.POStatus,
            targetOrder.fulfillmentPercent || 0,
            targetOrder.ETA,
            targetOrder.details,
            targetOrder.RequestDate,
            targetOrder.QuotationStatus || null,
          );

          const allocateProductionLines = [
            ...new Set(
              targetOrder.details
                .map((item: any) => item.allocateProductionLine)
                .filter(
                  (v: any) =>
                    v !== null && v !== undefined && v !== "" && v !== 99,
                ), // only filter undefined,empty or null values
            ),
          ];

          if (allocateProductionLines.length > 0) {
            targetOrder.allocateProductionLine = allocateProductionLines;
          }

          const total = targetOrder.TotalOrdered;
          const QtyToShip = calculateQtyToShip(total, targetOrder.details);
          targetOrder.QtyToShip = QtyToShip;
          targetOrder.FulfillmentPercent =
            total > 0 ? Math.min(((total - QtyToShip) / total) * 100, 100) : 0;
        }

        // Reflect updates in filteredOrders without overwriting the entire array
        const targetFilteredOrder = state.filteredOrders.find(
          (order: any) =>
            order.Key === key && order.OrderNumber1 === orderNumber,
        );
        if (targetFilteredOrder && targetOrder) {
          targetFilteredOrder.details = [...targetOrder.details];
          targetFilteredOrder.OrderStatus = targetOrder.OrderStatus;
          targetFilteredOrder.allocateProductionLine =
            targetOrder.allocateProductionLine;
          targetFilteredOrder.QtyToShip = targetOrder.QtyToShip;
          targetFilteredOrder.FulfillmentPercent =
            targetOrder.FulfillmentPercent;
        }
      })
      .addCase(createFactoryRunShipment.rejected, (state) => {
        state.loadingShipmentsAction = false;
      })

      // Update Run Shipment
      .addCase(updateFactoryRunShipment.pending, (state) => {
        state.loadingShipmentsAction = true;
      })
      .addCase(updateFactoryRunShipment.fulfilled, (state, action) => {
        state.loadingShipmentsAction = false;

        const { key, orderNumber, factoryOrderData } = action.payload.data;

        // Find the order in state.orders
        const targetOrder = state.approvedOrders.find(
          (order: any) =>
            order.Key === key && order.OrderNumber1 === orderNumber,
        );
        if (targetOrder) {
          // Initialize details array if it doesn't exist
          if (!Array.isArray(targetOrder.details)) {
            targetOrder.details = [];
          }
          // Push the new detail to the details array
          targetOrder.details = factoryOrderData;

          //  Use helper function here
          targetOrder.OrderStatus = calculateOrderStatus(
            targetOrder.POStatus,
            targetOrder.fulfillmentPercent || 0,
            targetOrder.ETA,
            targetOrder.details,
            targetOrder.RequestDate,
            targetOrder.QuotationStatus || null,
          );

          const allocateProductionLines = [
            ...new Set(
              targetOrder.details
                .map((item: any) => item.allocateProductionLine)
                .filter(
                  (v: any) =>
                    v !== null && v !== undefined && v !== "" && v !== 99,
                ), // only filter undefined,empty or null values
            ),
          ];

          if (allocateProductionLines.length > 0) {
            targetOrder.allocateProductionLine = allocateProductionLines;
          }

          const total = targetOrder.TotalOrdered;
          const QtyToShip = calculateQtyToShip(total, targetOrder.details);
          targetOrder.QtyToShip = QtyToShip;
          targetOrder.FulfillmentPercent =
            total > 0 ? Math.min(((total - QtyToShip) / total) * 100, 100) : 0;
        }

        // Reflect updates in filteredOrders without overwriting the entire array
        const targetFilteredOrder = state.filteredOrders.find(
          (order: any) =>
            order.Key === key && order.OrderNumber1 === orderNumber,
        );
        if (targetFilteredOrder && targetOrder) {
          targetFilteredOrder.details = [...targetOrder.details];
          targetFilteredOrder.OrderStatus = targetOrder.OrderStatus;
          targetFilteredOrder.allocateProductionLine =
            targetOrder.allocateProductionLine;
          targetFilteredOrder.QtyToShip = targetOrder.QtyToShip;
          targetFilteredOrder.FulfillmentPercent =
            targetOrder.FulfillmentPercent;
        }
      })
      .addCase(updateFactoryRunShipment.rejected, (state) => {
        state.loadingShipmentsAction = false;
      })

      .addCase(createDirectFactoryRunShipment.pending, (state) => {
        state.loadingShipmentsAction = true;
      })
      .addCase(createDirectFactoryRunShipment.fulfilled, (state, action) => {
        state.loadingShipmentsAction = false;
        const { key, orderNumber } = action.payload.data;
        // Find the order in state.orders
        const targetOrder = state.approvedOrders.find(
          (order: any) =>
            order.Key === key && order.OrderNumber1 === orderNumber,
        );
        if (targetOrder) {
          // Initialize details array if it doesn't exist
          if (!Array.isArray(targetOrder.details)) {
            targetOrder.details = [];
          }
          // Push the new detail to the details array
          targetOrder.details.push(action.payload.data);

          //  Use helper function here
          targetOrder.OrderStatus = calculateOrderStatus(
            targetOrder.POStatus,
            targetOrder.fulfillmentPercent || 0,
            targetOrder.ETA,
            targetOrder.details,
            targetOrder.RequestDate,
            targetOrder.QuotationStatus || null,
          );

          const allocateProductionLines = [
            ...new Set(
              targetOrder.details
                .map((item: any) => item.allocateProductionLine)
                .filter(
                  (v: any) =>
                    v !== null && v !== undefined && v !== "" && v !== 99,
                ), // only filter undefined,empty or null values
            ),
          ];

          if (allocateProductionLines.length > 0) {
            targetOrder.allocateProductionLine = allocateProductionLines;
          }

          const total = targetOrder.TotalOrdered;
          const QtyToShip = calculateQtyToShip(total, targetOrder.details);
          targetOrder.QtyToShip = QtyToShip;
          targetOrder.FulfillmentPercent =
            total > 0 ? Math.min(((total - QtyToShip) / total) * 100, 100) : 0;
        }

        // Reflect updates in filteredOrders without overwriting the entire array
        const targetFilteredOrder = state.filteredOrders.find(
          (order: any) =>
            order.Key === key && order.OrderNumber1 === orderNumber,
        );
        if (targetFilteredOrder && targetOrder) {
          targetFilteredOrder.details = [...targetOrder.details];
          targetFilteredOrder.OrderStatus = targetOrder.OrderStatus;
          targetFilteredOrder.allocateProductionLine =
            targetOrder.allocateProductionLine;
          targetFilteredOrder.QtyToShip = targetOrder.QtyToShip;
          targetFilteredOrder.FulfillmentPercent =
            targetOrder.FulfillmentPercent;
        }
      })
      .addCase(createDirectFactoryRunShipment.rejected, (state) => {
        state.loadingShipmentsAction = false;
      })

      .addCase(deleteFactoryRunShipment.pending, (state) => {
        state.loadingShipmentsAction = true;
      })
      .addCase(deleteFactoryRunShipment.fulfilled, (state, action) => {
        state.loadingShipmentsAction = false;

        const { key, orderNumber, factoryOrderData } = action.payload.data;

        // Find the order in state.orders
        const targetOrder = state.approvedOrders.find(
          (order: any) =>
            order.Key === key && order.OrderNumber1 === orderNumber,
        );
        if (targetOrder) {
          // Initialize details array if it doesn't exist
          if (!Array.isArray(targetOrder.details)) {
            targetOrder.details = [];
          }
          // Push the new detail to the details array
          targetOrder.details = factoryOrderData;

          //  Use helper function here
          targetOrder.OrderStatus = calculateOrderStatus(
            targetOrder.POStatus,
            targetOrder.fulfillmentPercent || 0,
            targetOrder.ETA,
            targetOrder.details,
            targetOrder.RequestDate,
            targetOrder.QuotationStatus || null,
          );

          const allocateProductionLines = [
            ...new Set(
              targetOrder.details
                .map((item: any) => item.allocateProductionLine)
                .filter(
                  (v: any) =>
                    v !== null && v !== undefined && v !== "" && v !== 99,
                ), // only filter undefined,empty or null values
            ),
          ];

          if (allocateProductionLines.length > 0) {
            targetOrder.allocateProductionLine = allocateProductionLines;
          }

          const total = targetOrder.TotalOrdered;
          const QtyToShip = calculateQtyToShip(total, targetOrder.details);
          targetOrder.QtyToShip = QtyToShip;
          targetOrder.FulfillmentPercent =
            total > 0 ? Math.min(((total - QtyToShip) / total) * 100, 100) : 0;
        }

        // Reflect updates in filteredOrders without overwriting the entire array
        const targetFilteredOrder = state.filteredOrders.find(
          (order: any) =>
            order.Key === key && order.OrderNumber1 === orderNumber,
        );
        if (targetFilteredOrder && targetOrder) {
          targetFilteredOrder.details = [...targetOrder.details];
          targetFilteredOrder.OrderStatus = targetOrder.OrderStatus;
          targetFilteredOrder.allocateProductionLine =
            targetOrder.allocateProductionLine;
          targetFilteredOrder.QtyToShip = targetOrder.QtyToShip;
          targetFilteredOrder.FulfillmentPercent =
            targetOrder.FulfillmentPercent;
        }
      })
      .addCase(deleteFactoryRunShipment.rejected, (state) => {
        state.loadingShipmentsAction = false;
      })

      // Get All Order Details with shipments
      .addCase(getAllChinaTrackerDetailsWithShipments.pending, (state) => {
        state.loadingShipmentsAction = true;
      })
      .addCase(
        getAllChinaTrackerDetailsWithShipments.fulfilled,
        (state, action) => {
          state.loadingShipmentsAction = false;
          state.orderDetailShipments = action.payload;
        },
      )
      .addCase(getAllChinaTrackerDetailsWithShipments.rejected, (state) => {
        state.loadingShipmentsAction = false;
      })

      // Run Order Interrupts
      .addCase(interruptRunOrderDetail.pending, (state) => {
        state.loadingInterruptsAction = true;
      })
      .addCase(interruptRunOrderDetail.fulfilled, (state, action) => {
        state.loadingInterruptsAction = false;

        const { factoryOrderDetail } = action.payload.data;
        const { key, orderNumber, _id, interrupted } = factoryOrderDetail;

        // 1. Find the target order
        const targetOrder = state.approvedOrders.find(
          (order: any) =>
            order.Key === key && order.OrderNumber1 === orderNumber,
        );

        if (!targetOrder || !Array.isArray(targetOrder.details)) {
          return;
        }

        // 2. Find the exact order detail by _id
        const targetDetail = targetOrder.details.find(
          (detail: any) => detail._id === _id,
        );

        if (!targetDetail) {
          return;
        }

        // 3. Update interrupted flag
        targetDetail.interrupted = interrupted;

        // 4. Recalculate order status
        targetOrder.OrderStatus = calculateOrderStatus(
          targetOrder.POStatus,
          targetOrder.fulfillmentPercent || 0,
          targetOrder.ETA,
          targetOrder.details,
          targetOrder.RequestDate,
          targetOrder.QuotationStatus || null,
        );

        // Reflect updates in filteredOrders without overwriting the entire array
        const targetFilteredOrder = state.filteredOrders.find(
          (order: any) =>
            order.Key === key && order.OrderNumber1 === orderNumber,
        );
        if (targetFilteredOrder && targetOrder) {
          targetFilteredOrder.details = [...targetOrder.details];
          targetFilteredOrder.OrderStatus = targetOrder.OrderStatus;
        }
      })
      .addCase(interruptRunOrderDetail.rejected, (state) => {
        state.loadingInterruptsAction = false;
      })

      .addCase(getTrackerInterruptsByKey.pending, (state) => {
        state.loadingInterrupts = true;
      })
      .addCase(getTrackerInterruptsByKey.fulfilled, (state, action) => {
        state.loadingInterrupts = false;
        state.interrupts = action.payload;
      })
      .addCase(getTrackerInterruptsByKey.rejected, (state) => {
        state.loadingInterrupts = false;
      })

      //  Search Interrupts
      .addCase(searchFactoryInterruptsByKey.pending, (state) => {
        state.loadingInterrupts = false;
      })
      .addCase(searchFactoryInterruptsByKey.fulfilled, (state, action) => {
        state.loadingInterrupts = false;
        state.interrupts = action.payload;
      })
      .addCase(searchFactoryInterruptsByKey.rejected, (state) => {
        state.loadingInterrupts = false;
      })

      //  Approve Cancel Orders
      .addCase(approveCancelledOrder.pending, (state) => {
        state.loadingCancel = true;
      })
      .addCase(approveCancelledOrder.fulfilled, (state, action) => {
        state.loadingCancel = false;
      })
      .addCase(approveCancelledOrder.rejected, (state) => {
        state.loadingCancel = false;
      })

      //  Cancelled Orders And Audit Approval
      .addCase(getAllCancelledOrders.pending, (state) => {
        state.loadingCancel = true;
      })
      .addCase(getAllCancelledOrders.fulfilled, (state, action) => {
        state.loadingCancel = false;
        state.cancelledOrdersHistory = action.payload.data;
        const historyKeys = new Set(
          action.payload.data.map((order: any) => order.key),
        );
        // Filter Out Cancelled Orders From History
        state.cancelledOrders = state.cancelledOrders.filter(
          (order: any) => !historyKeys.has(order.Key),
        );
      })
      .addCase(getAllCancelledOrders.rejected, (state) => {
        state.loadingCancel = false;
      })

      //  Shipment Screen

      // Get China Shipments By Container No
      .addCase(getChinaShipmentsByContainerNo.pending, (state) => {
        state.shipments.loadingChinaShipments = true;
      })
      .addCase(getChinaShipmentsByContainerNo.fulfilled, (state, action) => {
        state.shipments.loadingChinaShipments = false;
        state.shipments.chinaShipments = action.payload;
      })
      .addCase(getChinaShipmentsByContainerNo.rejected, (state) => {
        state.shipments.loadingChinaShipments = false;
      })

      // Get All China Shipments
      .addCase(getChinaShipments.pending, (state) => {
        state.shipments.loading = true;
      })
      .addCase(getChinaShipments.fulfilled, (state, action) => {
        state.shipments.loading = false;
        state.shipments.shipments = action.payload.data;
        state.shipments.totalCount = action.payload.pagination.totalCount;
        state.shipments.totalPages = action.payload.pagination.totalPages;
        state.shipments.pageSize = action.payload.pagination.pageSize;
        state.shipments.page = action.payload.pagination.page;
      })
      .addCase(getChinaShipments.rejected, (state) => {
        state.shipments.loading = false;
      })

      // Generate Shipment
      .addCase(generateChinaShipment.pending, (state) => {
        state.shipments.loadingAction = true;
      })
      .addCase(generateChinaShipment.fulfilled, (state, action) => {
        state.shipments.loadingAction = false;
      })
      .addCase(generateChinaShipment.rejected, (state) => {
        state.shipments.loadingAction = false;
      })

      // Update Generated Shipment
      .addCase(updateGeneratedChinaShipment.pending, (state) => {
        state.shipments.loadingAction = true;
      })
      .addCase(updateGeneratedChinaShipment.fulfilled, (state, action) => {
        state.shipments.loadingAction = false;
      })
      .addCase(updateGeneratedChinaShipment.rejected, (state) => {
        state.shipments.loadingAction = false;
      })

      // China Shipments (Incoming)

      // Get All China Shipments
      .addCase(getChinaIncomingShipments.pending, (state) => {
        state.shipments.incoming.loading = true;
      })
      .addCase(getChinaIncomingShipments.fulfilled, (state, action) => {
        state.shipments.incoming.loading = false;
        state.shipments.incoming.shipments = action.payload.data;
        state.shipments.incoming.totalCount =
          action.payload.pagination.totalCount;
        state.shipments.incoming.totalPages =
          action.payload.pagination.totalPages;
        state.shipments.incoming.pageSize = action.payload.pagination.pageSize;
        state.shipments.incoming.page = action.payload.pagination.page;
      })
      .addCase(getChinaIncomingShipments.rejected, (state) => {
        state.shipments.incoming.loading = false;
      })

      .addCase(approveIncomingChinaShipment.pending, (state) => {
        state.shipments.incoming.loadingAction = true;
      })
      .addCase(approveIncomingChinaShipment.fulfilled, (state, action) => {
        state.shipments.incoming.loadingAction = false;
      })
      .addCase(approveIncomingChinaShipment.rejected, (state) => {
        state.shipments.incoming.loadingAction = false;
      });
  },
});

// Export actions
export const {
  searchTrackerOrders,
  resetSearch,
  filterTrackerOrders,
  sortTrackerOrdersByOrderNumber,
  resetEnquiries,
  filterSpecificField,
  resetIncomingSearch,
  searchIncomingTrackerOrders,
  resetRejectedSearch,
  searchRejectedTrackerOrders,
  setChinaLanguage,
  resetUnrevisedRejectedSearch,
  searchUnRevisedRejectedTrackerOrders,
  searchAllTrackerOrders,
  resetAllSearch,
  searchCancelledTrackerOrders,
  resetCancelledSearch,
  searchCancelledTrackerAuditOrders,
  enrichChinaShipments,

  // Shipments
  updateSort,
  updateStatus,
  updatePage,
  updatePageSize,
  searchShipment,
  searchIncomingShipment,

  updateIncomingSort,
  updateIncomingStatus,
  updateIncomingPage,
  updateIncomingPageSize,
  enrichChinaIncomingShipments,

  filterAllSearch,
} = chinaTrackerSlice.actions;

// Export reducer
export default chinaTrackerSlice.reducer;
